import { Component, signal } from '@angular/core';
import { Validators } from '@angular/forms';

import { BehaviorSubject, Subject } from 'rxjs';

import { CamStepFormComponent, CamStepFormService, FormComponent, ICamStepFormStep } from '@camelot/form-basic';
import { TextBoxComponent } from '@camelot/form-input';
import { InputEmail, CamAbstractInput, InputNumber, InputPassword, InputTextBox } from '@camelot/form-model';

import { TestIdDirective } from '@camelot/testing-harness';

interface StepData {
  first?: string;
  second?: string;
}

@Component({
    imports: [CamStepFormComponent, FormComponent, TestIdDirective, TextBoxComponent],
    template: `
    <cam-form
      camTestId="fv-required-form"
      [inputs]="this.requiredInputs"
      [askValidation$]="this.askValidation$"
      (valid)="this.onValid($event)"
      (isFormValid)="this.formValid.set($event)"
    />
    <div camTestId="fv-required-submitted">{{ this.submitted() }}</div>
    <div camTestId="fv-required-valid">{{ this.formValid() }}</div>
    <button camTestId="fv-ask-validation" (click)="this.askValidation$.next(null)">validate</button>

    <cam-input-textbox camTestId="fv-email" [input]="this.email" [standalone]="true" />
    <cam-input-textbox camTestId="fv-password" [input]="this.password" [standalone]="true" />
    <cam-input-textbox camTestId="fv-number" [input]="$any(this.number)" [standalone]="true" />

    <cam-form camTestId="fv-visible-form" [inputs]="this.visibleFormInputs" />
    <button camTestId="fv-toggle-visible" (click)="this.toggleVisible()">toggle</button>

    <cam-input-textbox camTestId="fv-readonly" [input]="this.readonlyInput" [standalone]="true" />
    <button camTestId="fv-set-readonly" (click)="this.readonlyInput.setReadonly(true)">readonly</button>

    <cam-form camTestId="fv-live-form" [inputs]="this.liveInputs" [onLive]="true" (valid)="this.onLive($event)" />
    <div camTestId="fv-live-value">{{ this.liveValue() }}</div>

    <cam-step-form camTestId="fv-step-form" [steps]="this.steps" [service]="this.stepService" />
  `
})
export class FormValidationCase {
  public askValidation$ = new Subject<null>();
  public email = new InputEmail({ key: 'email', label: 'Email' });
  public formValid = signal<boolean | null>(null);
  public liveInputs: CamAbstractInput[] = [new InputTextBox({ key: 'live', label: 'Live' })];
  public liveValue = signal('');
  public number = new InputNumber({ key: 'number', label: 'Number' });
  public password = new InputPassword({ key: 'password', label: 'Password' });
  public readonlyInput = new InputTextBox({ key: 'readonly', label: 'Readonly' });
  public requiredInputs: CamAbstractInput[] = [
    new InputTextBox({ key: 'requiredField', label: 'Required field', validators: [Validators.required] }),
  ];
  public stepService = new CamStepFormService<StepData>();
  public steps: ICamStepFormStep<StepData>[] = [
    { form: () => [new InputTextBox({ key: 'first', label: 'First step field' })], title: 'step-one-title' },
    { form: () => [new InputTextBox({ key: 'second', label: 'Second step field' })], title: 'step-two-title' },
  ];
  public submitted = signal('');
  public togglable = new InputTextBox({ key: 'togglable', label: 'Togglable', visible$: this.createVisible$() });
  public visibleFormInputs: CamAbstractInput[] = [this.togglable];

  private _visible$!: BehaviorSubject<boolean>;

  public onLive(data: unknown): void {
    this.liveValue.set(JSON.stringify(data));
  }

  public onValid(data: unknown): void {
    this.submitted.set(JSON.stringify(data));
  }

  public toggleVisible(): void {
    this._visible$.next(!this._visible$.value);
  }

  private createVisible$(): BehaviorSubject<boolean> {
    this._visible$ = new BehaviorSubject<boolean>(true);
    return this._visible$;
  }
}
